import { useCallback, useEffect, useRef, useState } from "react";
import ExcelJS from "exceljs";
import { RefreshCw, ChevronLeft, ChevronRight, Download, Upload, FileText, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import FilterPills from "./admin/FilterPills";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { useWarehouseScope } from "../access/useWarehouseScope";

const PAGE_SIZE = 25;
const DETAILS_CHUNK = 500;

const DETAIL_HEADER = [
    "action", "detail_id", "warehouse", "sku", "location",
    "quantity", "unit_cost", "currency",
    "received_at", "po_number", "supplier_name", "status", "notes",
];

interface BulkResult {
    added: number;
    updated: number;
    removed: number;
    errors: { row: number; reason: string }[];
}

// Identifier columns that round-trip as Text — covers every tab's
// header (stock / detail / locations / DETAIL_HEADER template). detail_id
// is included because a 16+ digit detail_id would otherwise come back as
// scientific notation; sku/location/tote/po_number all carry digit-heavy
// strings that Excel would happily reformat. Numeric columns (quantities,
// costs, walk-orders) are deliberately left untouched so admins can
// sort / filter on them in Excel.
const TEXT_COLUMNS = new Set<string>([
    "detail_id", "sku", "barcode", "location", "tote", "po_number",
]);

const cellToString = (cell: ExcelJS.Cell | undefined): string => {
    if (!cell) return "";
    const v = cell.value;
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === "object") {
        if ("result" in v && (v as { result?: unknown }).result != null) {
            return String((v as { result: unknown }).result);
        }
        if ("text" in v && (v as { text?: unknown }).text != null) {
            return String((v as { text: unknown }).text);
        }
        if ("richText" in v) {
            const rt = (v as { richText?: { text: string }[] }).richText;
            return Array.isArray(rt) ? rt.map(r => r.text).join("") : "";
        }
    }
    return String(v);
};

const readXlsxRows = async (file: File): Promise<{ header: string[]; rows: string[][] }> => {
    const buffer = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);
    const ws = wb.worksheets[0];
    if (!ws || ws.rowCount < 1) return { header: [], rows: [] };
    const headerRow = ws.getRow(1);
    const colCount = headerRow.cellCount;
    const header: string[] = [];
    for (let c = 1; c <= colCount; c++) {
        header[c - 1] = cellToString(headerRow.getCell(c)).trim().toLowerCase();
    }
    const rows: string[][] = [];
    for (let r = 2; r <= ws.rowCount; r++) {
        const xrow = ws.getRow(r);
        const cells: string[] = [];
        for (let c = 1; c <= colCount; c++) cells[c - 1] = cellToString(xrow.getCell(c));
        if (cells.some(s => s.trim() !== "")) rows.push(cells);
    }
    return { header, rows };
};

type Tab = "stock" | "detail" | "locations";

interface InventoryRow {
    sku: string;
    // First barcode of the SKU's scannable list (product barcode, else
    // oldest alias). Display-only; null when the SKU has none.
    barcode?: string | null;
    warehouse: string;
    // total = unsellable + allocated + on_hand. Every unit is in exactly
    // one of those three states; the split is recomputed by the server
    // after every receive / put-away / order allocation.
    //   unsellable — at non-sellable / deleted / unknown locations
    //   allocated  — claimed by a pick wave (pinned to locations)
    //   on_hand    — sellable + uncommitted
    total: number;
    unsellable: number;
    // Units at sellable locations flagged exclude_from_channels (sale
    // pallets, migration 192): real stock, but neither pickable on_hand
    // nor pushed to channels. total = unsellable + channel_excluded +
    // allocated + on_hand.
    channel_excluded?: number;
    allocated: number;
    on_hand: number;
    // Sold on a channel but not yet claimed by a wave — no location is
    // committed yet, so reserved lives OUTSIDE the total split above.
    // Channel availability is pushed as max(0, on_hand − reserved).
    reserved: number;
    total_cost: number;
    supplier_name: string | null;
    updated_at: string;
}

interface InventoryDetailRow {
    detail_id: number;
    sku: string;
    // See InventoryRow.barcode.
    barcode?: string | null;
    warehouse: string;
    location: string;
    // Human-readable name of the location from the locations table; the
    // `location` field above carries the barcode. Null for IN-TRANSIT /
    // unknown / deleted locations that have no matching locations row.
    location_name?: string | null;
    quantity: number;
    remaining_qty: number;
    // Open allocations against this receipt row (inventory_allocation
    // rows with released_at NULL) — the same "allocated" definition the
    // Summary aggregate and Locations rollup use. NOT the historical
    // (quantity − remaining_qty) gap, which also counts picked/shipped/
    // moved units.
    allocated_qty: number;
    // Display-only FIFO projection of the SKU's sold-but-unwaved
    // (reserved) units onto this receipt row — oldest received first,
    // sellable rows only. The wave may pick different locations; the
    // projection reshuffles on the next load. Never authoritative.
    reserved_projected?: number;
    unit_cost: number;
    currency: string;
    received_at: string;
    received_by_uid?: string | null;
    received_by_name?: string | null;
    po_number?: string | null;
    // Canonical supplier from the products table (kept in sync with the
    // Inventory filter dropdown, which is also keyed off products).
    supplier_name?: string | null;
    // Receipt-time supplier captured when the inventory_detail row was
    // first inserted — preserved for historical attribution; rarely
    // surfaced in the UI but available on the row for export/audit.
    receipt_supplier_name?: string | null;
    // Catalogue unit weight (products.weight_grams). Export-only: not
    // shown in the Detail table.
    unit_weight_grams?: number | null;
    // Tote that brought the items in at book-in. Null on legacy rows
    // (pre-migration 013) and on rows from the manual receive form.
    tote_id?: string | null;
    status: string;
    notes?: string | null;
    // Resolved server-side from the location's sellable flag (with the
    // same unknown→unsellable fallback the aggregate uses). Lets the FE
    // skip its own location lookup map.
    sellable: Sellable;
    // TRUE when the location is sellable but excluded from channels (sale
    // pallet): the row's units are "on pallet", not on_hand.
    channel_excluded?: boolean;
}

type Sellable = "sellable" | "unsellable";

interface LocationsRow {
    sku: string;
    // See InventoryRow.barcode.
    barcode?: string | null;
    warehouse: string;
    location: string;
    // See InventoryDetailRow.location_name — same barcode/name split.
    location_name?: string | null;
    on_hand: number;
    allocated: number;
    unsellable: number;
    channel_excluded?: number;
    total: number;
    // See InventoryDetailRow.reserved_projected — same FIFO projection,
    // summed to this (sku, warehouse, location) bucket.
    reserved_projected?: number;
    total_cost: number;
    sellable: Sellable;
    supplier_name: string | null;
    // Comma-separated distinct tote IDs that contributed receipts to
    // this bucket. Null when no booked-in row contributed (manual
    // receive-form receipts have no tote_id).
    tote_id: string | null;
}

const formatMoney = (value: number, currency = "GBP") => {
    try { return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value); }
    catch { return `${currency} ${value.toFixed(2)}`; }
};

// Format an ISO timestamp as dd/mm/yyyy hh:mm:ss in local time. Used
// for received_at on the Detail tab and the matching CSV export so the
// shape is consistent between display and export — and so admins
// outside the en-US locale (which is what toLocaleString defaulted to
// some browsers) see the UK-style date everywhere.
const formatDateTime = (iso: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} `
        + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// Pseudo-warehouse pills that expand into a list of real warehouses
// when the BE call goes out. "UK" covers Newport + Pembroke; add
// more groups (EU, etc.) here as the network grows. The pill values
// are unique IDs prefixed with `group:` so they can't collide with a
// real warehouse name.
const WAREHOUSE_GROUPS: Record<string, string[]> = {
    "group:UK": ["Newport", "Pembroke"],
};
const expandWarehouseFilter = (raw: string): string[] => {
    if (!raw) return [];
    return WAREHOUSE_GROUPS[raw] ?? [raw];
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const downloadXlsx = async (
    filename: string,
    sheetName: string,
    header: readonly string[],
    rows: (string | number)[][],
) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(sheetName);
    ws.addRow(header as string[]);
    for (const r of rows) ws.addRow(r);
    header.forEach((name, i) => {
        if (TEXT_COLUMNS.has(name)) ws.getColumn(i + 1).numFmt = "@";
    });
    const buffer = await wb.xlsx.writeBuffer();
    triggerBlobDownload(new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }), filename);
};

// Inventory admin — three views over the same (sku, warehouse) filter:
//   • Stock     — the per-(sku, warehouse) aggregate from /inventory.
//   • Detail    — every live inventory_detail row from /inventory/details
//                 (one row per receipt, so PO/cost/received-at all show).
//   • Locations — same details rolled up to (sku, warehouse, location)
//                 so each SKU appears once per physical location, with
//                 PO collapsed away.
const InventoryAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [tab, setTab] = useState<Tab>("stock");
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouseFilter] = useState<string>("");
    // Without cross_warehouse the view is pinned to the person's own warehouse (no All, no groups).
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [warehouses, setWarehouses] = useState<string[]>([]);
    // Single search box; per-tab the BE matches:
    //   stock     — sku
    //   detail    — sku / location / po_number
    //   locations — sku / location
    // Commits on Enter / blur so we don't fire a request on every
    // keystroke. `?search=` URL param lets AdminSearch deep-link in
    // pre-filtered.
    const [searchFilter, setSearchFilter] = useUrlSearchParam("search");
    const [searchInput, setSearchInput] = useState(searchFilter);
    useEffect(() => { setSearchInput(searchFilter); }, [searchFilter]);
    // Sellable filter only applies on Detail and Locations. Stock has
    // unsellable folded into the aggregate; filtering it there would
    // hide rows that have any sellable units mixed in.
    const [sellableFilter, setSellableFilter] = useState<"" | Sellable>("");
    // Supplier filter sourced from products.supplier_name. Populated
    // once on mount from /products/suppliers; the input is a searchable
    // datalist combobox so admins can either scroll or type to narrow.
    // Commits on Enter / blur, mirroring the search field.
    const [supplierFilter, setSupplierFilter] = useState<string>("");
    const [supplierInput, setSupplierInput] = useState<string>("");
    const [supplierOptions, setSupplierOptions] = useState<string[]>([]);
    const [page, setPage] = useState(0);

    const [aggregate, setAggregate] = useState<InventoryRow[]>([]);
    const [details, setDetails] = useState<InventoryDetailRow[]>([]);
    const [locations, setLocations] = useState<LocationsRow[]>([]);
    const [aggregateTotal, setAggregateTotal] = useState(0);
    const [detailsTotal, setDetailsTotal] = useState(0);
    const [locationsTotal, setLocationsTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const [exporting, setExporting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);
    const [uploadResult, setUploadResult] = useState<BulkResult | null>(null);
    const detailFileInputRef = useRef<HTMLInputElement>(null);

    // Reset to first page whenever any filter changes — the current page
    // index is meaningless against a different filter set.
    useEffect(() => { setPage(0); }, [tab, warehouseFilter, searchFilter, sellableFilter, supplierFilter]);
    useEffect(() => { setUploadResult(null); }, [tab]);
    // Clear the sellable filter when leaving a tab that supports it so
    // re-entering Detail/Locations starts at "all".
    useEffect(() => { if (tab === "stock") setSellableFilter(""); }, [tab]);

    // Build the shared query-string for all three list endpoints. The
    // sellable filter is only included on the tabs that accept it; the
    // BE ignores unknown values but skipping it keeps the URL clean.
    // `paging` overrides the on-screen page/size — used by the export
    // loop, which walks the full result set instead of the visible page.
    const buildParams = useCallback((
        includeSellable: boolean,
        paging?: { page: number; pageSize: number },
    ) => {
        const p = new URLSearchParams();
        const expanded = expandWarehouseFilter(warehouseFilter);
        if (expanded.length > 0) p.set("warehouse", expanded.join(","));
        if (searchFilter.trim()) p.set("q", searchFilter.trim());
        if (includeSellable && sellableFilter) p.set("sellable", sellableFilter);
        if (supplierFilter.trim()) p.set("supplier", supplierFilter.trim());
        p.set("page", String(paging ? paging.page : page));
        p.set("page_size", String(paging ? paging.pageSize : PAGE_SIZE));
        return p;
    }, [warehouseFilter, searchFilter, sellableFilter, supplierFilter, page]);

    const loadStock = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/inventory?${buildParams(false)}`, {});
            if (res.ok) {
                const data = await res.json();
                setAggregate(Array.isArray(data.inventory) ? data.inventory : []);
                setAggregateTotal(typeof data.total === "number" ? data.total : 0);
            }
        } catch {
            toast.error(t("server_error"), { toastId: "inventory-load-err" });
        } finally {
            setLoading(false);
        }
    }, [buildParams, t]);

    const loadDetails = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/inventory/details?${buildParams(true)}`, {});
            if (res.ok) {
                const data = await res.json();
                setDetails(Array.isArray(data.details) ? data.details : []);
                setDetailsTotal(typeof data.total === "number" ? data.total : 0);
            }
        } catch {
            toast.error(t("server_error"), { toastId: "inventory-load-err" });
        } finally {
            setLoading(false);
        }
    }, [buildParams, t]);

    const loadLocations = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/inventory/locations?${buildParams(true)}`, {});
            if (res.ok) {
                const data = await res.json();
                setLocations(Array.isArray(data.locations) ? data.locations : []);
                setLocationsTotal(typeof data.total === "number" ? data.total : 0);
            }
        } catch {
            toast.error(t("server_error"), { toastId: "inventory-load-err" });
        } finally {
            setLoading(false);
        }
    }, [buildParams, t]);

    const refresh = useCallback(() => {
        if (tab === "stock")     return loadStock();
        if (tab === "detail")    return loadDetails();
        return loadLocations();
    }, [tab, loadStock, loadDetails, loadLocations]);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const data: { warehouse: string }[] = await res.json();
                setWarehouses(data.map(r => r.warehouse).sort());
            } catch { /* leave empty */ }
        })();
    }, []);

    // Suppliers come from the products catalogue, fetched once and
    // cached in state. The filter input is a free-text search bound
    // to a datalist of these values — admins can scroll the full list
    // or type to narrow without us re-querying the BE per keystroke.
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/products/suppliers`, {});
                if (!res.ok) return;
                const data: { suppliers: string[] } = await res.json();
                setSupplierOptions(Array.isArray(data.suppliers) ? data.suppliers : []);
            } catch { /* leave empty */ }
        })();
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const activeRows = tab === "stock" ? aggregate : tab === "detail" ? details : locations;
    const activeTotal = tab === "stock" ? aggregateTotal : tab === "detail" ? detailsTotal : locationsTotal;

    const totalPages = Math.max(1, Math.ceil(activeTotal / PAGE_SIZE));
    const safePage = Math.min(page, Math.max(totalPages - 1, 0));
    const rangeFrom = activeTotal === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const rangeTo = Math.min((safePage + 1) * PAGE_SIZE, activeTotal);

    // Warehouse pill counts come from the /settings list rather than
    // counting rows on the current page (which would always show 0–25).
    // Group pills (e.g. UK = Newport + Pembroke) sit at the front
    // and only render when at least one underlying warehouse is in scope.
    const warehouseOptions = (() => {
        if (!warehouseScope.canCross) return warehouseScope.limit(warehouses).map(w => ({ value: w, label: w }));
        const groupOptions = Object.entries(WAREHOUSE_GROUPS)
            .filter(([, members]) => members.some(m => warehouses.includes(m)))
            .map(([groupKey]) => ({
                value: groupKey,
                label: groupKey.replace(/^group:/, ""),
            }));
        const individual = warehouses.map(w => ({ value: w, label: w }));
        return [...groupOptions, ...individual];
    })();

    // Server-side export: GET /inventory/export streams one .xlsx with
    // the active tab's filters and every matching row, in the same
    // columns as before. Replaces the old loop that paged the whole
    // result set through the list endpoints as JSON (25–80 MB for a full
    // warehouse, minutes per export) and built the workbook in the browser.
    const exportXlsx = async () => {
        if (activeRows.length === 0 || exporting) return;
        setExporting(true);
        try {
            const view = tab === "stock" ? "stock" : tab === "detail" ? "detail" : "locations";
            const params = buildParams(tab !== "stock");
            params.delete("page");
            params.delete("page_size");
            params.set("view", view);
            const res = await apiFetch(`/inventory/export?${params}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const stamp = new Date().toISOString().slice(0, 10);
            triggerBlobDownload(blob, `inventory-${view}-${stamp}.xlsx`);
        } catch {
            toast.error(t("server_error"), { toastId: "inventory-export-err" });
        } finally {
            setExporting(false);
        }
    };

    const downloadDetailTemplate = async () => {
        // Examples mirror the manifest template pattern: one row per action
        // so admins can adapt them in place. Update + remove only need
        // detail_id; the rest of the columns can be blank.
        const rows: (string | number)[][] = [
            ["add", "", "Newport", "JLI-WH-00482", "NPA101", "12", "1850.00", "GBP", "2026-04-30", "PO-2026-001", "Dyson Limited", "available", "Initial receipt"],
            ["update", "12345", "", "", "NPA102", "", "1900.00", "", "", "", "", "", "Re-priced + relocated"],
            ["remove", "67890", "", "", "", "", "", "", "", "", "", "", ""],
        ];
        await downloadXlsx("inventory-detail-template.xlsx", "Detail", DETAIL_HEADER, rows);
    };

    const onDetailFileChosen = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const file = ev.target.files?.[0];
        if (!file) return;
        ev.target.value = "";

        let parsed: { header: string[]; rows: string[][] };
        try {
            parsed = await readXlsxRows(file);
        } catch {
            toast.error(t("inventory_detail_upload_parse_error"), { toastId: "inv-det-upload-parse" });
            return;
        }
        const { header, rows: dataRows } = parsed;
        if (dataRows.length === 0) {
            toast.error(t("inventory_detail_upload_empty"), { toastId: "inv-det-upload-empty" });
            return;
        }
        const idx = (name: string) => header.indexOf(name);
        const actionIdx = idx("action");
        const skuIdx = idx("sku");
        if (actionIdx === -1 || (skuIdx === -1 && idx("detail_id") === -1)) {
            toast.error(t("inventory_detail_upload_bad_header"), { toastId: "inv-det-upload-hdr" });
            return;
        }
        const cell = (r: string[], i: number) => (i >= 0 ? (r[i] ?? "") : "");
        const rowsToSend = dataRows.map(r => ({
            action:        cell(r, actionIdx),
            detail_id:     cell(r, idx("detail_id")),
            sku:           cell(r, skuIdx),
            warehouse:     cell(r, idx("warehouse")),
            location:      cell(r, idx("location")),
            quantity:      cell(r, idx("quantity")),
            unit_cost:     cell(r, idx("unit_cost")),
            currency:      cell(r, idx("currency")),
            received_at:   cell(r, idx("received_at")),
            po_number:     cell(r, idx("po_number")),
            supplier_name: cell(r, idx("supplier_name")),
            status:        cell(r, idx("status")),
            notes:         cell(r, idx("notes")),
        }));

        setUploading(true);
        setUploadProgress({ done: 0, total: rowsToSend.length });
        setUploadResult(null);

        const combined: BulkResult = { added: 0, updated: 0, removed: 0, errors: [] };
        try {
            for (let i = 0; i < rowsToSend.length; i += DETAILS_CHUNK) {
                const chunk = rowsToSend.slice(i, i + DETAILS_CHUNK);
                const res = await apiFetch(`/inventory/details/bulk`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ rows: chunk }),
                });
                // 413 = chunk body exceeds the BE's JSON limit. Stop the
                // upload (subsequent chunks will fail the same way) and
                // surface a specific message so the operator knows it's
                // a size issue.
                if (res.status === 413) {
                    toast.error(t("bulk_upload_too_large"), { toastId: "inventory-upload-413" });
                    break;
                }
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const result: BulkResult = await res.json();
                combined.added += result.added;
                combined.updated += result.updated;
                combined.removed += result.removed;
                for (const e of result.errors) {
                    combined.errors.push({ row: e.row + i, reason: e.reason });
                }
                setUploadProgress({
                    done: Math.min(i + chunk.length, rowsToSend.length),
                    total: rowsToSend.length,
                });
            }
            setUploadResult(combined);
            await loadDetails();
        } catch {
            toast.error(t("server_error"), { toastId: "inv-det-upload-err" });
        } finally {
            setUploading(false);
            setUploadProgress(null);
        }
    };

    const tabButton = (key: Tab, labelKey: string) => (
        <button
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`filter-pill${tab === key ? " filter-pill--active" : ""}`}
            onClick={() => setTab(key)}
        >
            {t(labelKey)}
        </button>
    );

    // BE returns the page slice directly now — no client-side trimming.
    const visibleStock = aggregate;
    const visibleDetail = details;
    const visibleLocations = locations;

    return (
        <div className="inventory-admin">
            <div className="inventory-admin-tabs" role="tablist">
                {tabButton("stock", "inventory_tab_stock")}
                {tabButton("detail", "inventory_tab_detail")}
                {tabButton("locations", "inventory_tab_locations")}
                {tab === "detail" && (
                    <>
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm inventory-admin-tabs-refresh"
                            onClick={downloadDetailTemplate}
                        >
                            <FileText size={14} /> {t("inventory_detail_download_template")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => detailFileInputRef.current?.click()}
                            disabled={uploading || readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            <Upload size={14} /> {uploading ? t("submitting") : t("inventory_detail_upload_xlsx")}
                        </button>
                        <input
                            ref={detailFileInputRef}
                            type="file"
                            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            style={{ display: "none" }}
                            onChange={onDetailFileChosen}
                        />
                    </>
                )}
                <button
                    type="button"
                    className={`btn btn-ghost btn-sm${tab === "detail" ? "" : " inventory-admin-tabs-refresh"}`}
                    onClick={exportXlsx}
                    disabled={loading || exporting || activeRows.length === 0}
                >
                    <Download size={14} /> {exporting ? t("inventory_exporting") : t("inventory_export_xlsx")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={refresh}
                    disabled={loading}
                >
                    <RefreshCw size={14} /> {t("refresh")}
                </button>
            </div>

            {uploadProgress && (
                <div className="totes-admin-progress">
                    <div className="totes-admin-progress-label">
                        {t("totes_upload_progress", {
                            done: String(uploadProgress.done),
                            total: String(uploadProgress.total),
                        })}
                        <span className="totes-admin-progress-pct">
                            {Math.round((uploadProgress.done / Math.max(uploadProgress.total, 1)) * 100)}%
                        </span>
                    </div>
                    <div className="totes-admin-progress-track">
                        <div
                            className="totes-admin-progress-fill"
                            style={{ width: `${(uploadProgress.done / Math.max(uploadProgress.total, 1)) * 100}%` }}
                        />
                    </div>
                </div>
            )}

            {uploadResult && (
                <div className="totes-admin-result">
                    <div className="totes-admin-result-line">
                        <CheckCircle strokeWidth={3} size={16} className="totes-admin-result-ok" />
                        <span>
                            {t("totes_bulk_summary", {
                                added: String(uploadResult.added),
                                updated: String(uploadResult.updated),
                                removed: String(uploadResult.removed),
                            })}
                        </span>
                    </div>
                    {uploadResult.errors.length > 0 && (
                        <ul className="totes-admin-errors">
                            {uploadResult.errors.map((e, i) => (
                                <li key={i}>
                                    <AlertCircle size={14} />
                                    <span>{t("totes_row_label", { row: String(e.row) })}: {e.reason}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            <FilterPills
                className="warehouse-admin-pills"
                value={warehouseFilter}
                onChange={setWarehouseFilter}
                allCount={activeRows.length}
                hideAll={!warehouseScope.canCross}
                options={warehouseOptions}
            />

            <div className="warehouse-admin-filters users-admin-filters">
                <input
                    className="locations-table-search wh-admin-filter-search"
                    type="search"
                    placeholder={
                        tab === "detail"
                            ? t("inventory_filter_detail_hint")
                            : tab === "locations"
                                ? t("inventory_filter_locations_hint")
                                : t("inventory_filter_sku_hint")
                    }
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setSearchFilter(searchInput.trim());
                        }
                    }}
                    onBlur={() => {
                        if (searchInput.trim() !== searchFilter.trim()) {
                            setSearchFilter(searchInput.trim());
                        }
                    }}
                />
                {/* Supplier combobox: typing narrows the datalist; clearing
                    the input re-shows the full list (browser-native). The
                    filter applies on every tab — Stock joins through the
                    products table on the BE side. Commits on Enter / blur
                    so admins finish typing before the BE re-queries. */}
                <input
                    className="locations-table-search wh-admin-filter-supplier"
                    list="inventory-supplier-options"
                    type="search"
                    placeholder={t("inventory_filter_supplier_hint")}
                    value={supplierInput}
                    onChange={e => setSupplierInput(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setSupplierFilter(supplierInput.trim());
                        }
                    }}
                    onBlur={() => {
                        if (supplierInput.trim() !== supplierFilter.trim()) {
                            setSupplierFilter(supplierInput.trim());
                        }
                    }}
                />
                <datalist id="inventory-supplier-options">
                    {supplierOptions.map(s => <option key={s} value={s} />)}
                </datalist>
                {(tab === "detail" || tab === "locations") && (
                    <select
                        className="locations-table-search wh-admin-filter-select"
                        value={sellableFilter}
                        onChange={e => setSellableFilter(e.target.value as "" | Sellable)}
                    >
                        <option value="">{t("inventory_filter_sellable_all")}</option>
                        <option value="sellable">{t("locations_value_sellable")}</option>
                        <option value="unsellable">{t("locations_value_unsellable")}</option>
                    </select>
                )}
            </div>

            <div className="locations-table-wrap">
                {/* inventory-admin-table opts out of .locations-table's
                    per-column nth-child widths (sized for the Locations
                    admin grid) and applies its own — see admin-settings.css. */}
                <table className={`locations-table inventory-admin-table inventory-admin-table--${tab}`}>
                    {tab === "stock" && (
                        <>
                            <thead>
                                <tr>
                                    <th>{t("inventory_col_warehouse")}</th>
                                    <th>{t("inventory_col_sku")}</th>
                                    <th>{t("inventory_col_barcode")}</th>
                                    <th className="locations-table-qty">{t("inventory_col_on_hand")}</th>
                                    <th className="locations-table-qty">{t("inventory_col_allocated")}</th>
                                    <th className="locations-table-qty" title={t("inventory_reserved_hint")}>{t("inventory_col_reserved")}</th>
                                    <th className="locations-table-qty">{t("inventory_col_unsellable")}</th>
                                    <th className="locations-table-qty" title={t("inventory_on_pallet_hint")}>{t("inventory_col_on_pallet")}</th>
                                    <th className="locations-table-qty">{t("inventory_col_total")}</th>
                                    <th className="locations-table-num">{t("inventory_col_unit_cost")}</th>
                                    <th className="locations-table-num">{t("inventory_col_total_cost")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleStock.map(r => {
                                    // Weighted-average unit cost across the rolled-up
                                    // receipts contributing to this (sku, warehouse)
                                    // bucket. Falls back to "—" when total quantity
                                    // is 0/missing so we don't render NaN/Infinity.
                                    const totalQty = Number(r.total) || 0;
                                    const totalCost = Number(r.total_cost) || 0;
                                    const unitCost = totalQty > 0 ? totalCost / totalQty : null;
                                    return (
                                        <tr key={`${r.sku}|${r.warehouse}`}>
                                            <td>{r.warehouse}</td>
                                            <td className="locations-table-mono">{r.sku}</td>
                                            <td className="locations-table-mono">{r.barcode || "—"}</td>
                                            <td className="locations-table-qty">{r.on_hand}</td>
                                            <td className="locations-table-qty">{r.allocated}</td>
                                            <td className="locations-table-qty">{Number(r.reserved) || 0}</td>
                                            <td className="locations-table-qty">{r.unsellable}</td>
                                            <td className="locations-table-qty">{Number(r.channel_excluded) || 0}</td>
                                            <td className="locations-table-qty">{r.total}</td>
                                            <td className="locations-table-num">{unitCost != null ? formatMoney(unitCost) : "—"}</td>
                                            <td className="locations-table-num">{formatMoney(totalCost)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </>
                    )}

                    {tab === "detail" && (
                        <>
                            <thead>
                                <tr>
                                    <th>{t("inventory_col_warehouse")}</th>
                                    <th>{t("inventory_col_sku")}</th>
                                    <th>{t("inventory_col_barcode")}</th>
                                    <th>{t("inventory_col_location")}</th>
                                    <th>{t("inventory_col_tote")}</th>
                                    <th>{t("inventory_col_po")}</th>
                                    <th>{t("inventory_col_received")}</th>
                                    <th className="locations-table-qty">{t("inventory_col_on_hand")}</th>
                                    <th className="locations-table-qty">{t("inventory_col_allocated")}</th>
                                    <th className="locations-table-qty" title={t("inventory_reserved_projected_hint")}>{t("inventory_col_reserved_projected")}</th>
                                    <th className="locations-table-qty">{t("inventory_col_unsellable")}</th>
                                    <th className="locations-table-qty" title={t("inventory_on_pallet_hint")}>{t("inventory_col_on_pallet")}</th>
                                    <th className="locations-table-qty">{t("inventory_col_total")}</th>
                                    <th className="locations-table-num">{t("inventory_col_unit_cost")}</th>
                                    <th className="locations-table-num">{t("inventory_col_total_cost")}</th>
                                    <th>{t("inventory_col_sellable")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleDetail.map(r => {
                                    const remaining = Number(r.remaining_qty) || 0;
                                    const unitCost = Number(r.unit_cost) || 0;
                                    const sellable = r.sellable;
                                    const onPallet = sellable === "sellable" && r.channel_excluded ? remaining : 0;
                                    const onHand = sellable === "sellable" ? remaining - onPallet : 0;
                                    const unsellable = sellable === "sellable" ? 0 : remaining;
                                    // Real open allocations from the BE, not the
                                    // (quantity − remaining) gap — that gap also
                                    // counts picked/shipped/moved units, which
                                    // showed phantom allocation on receipts whose
                                    // stock had simply left. Total is derived the
                                    // same way as Summary/Locations so the views
                                    // reconcile.
                                    const allocated = Number(r.allocated_qty) || 0;
                                    return (
                                        <tr key={r.detail_id}>
                                            <td>{r.warehouse}</td>
                                            <td className="locations-table-mono">{r.sku}</td>
                                            <td className="locations-table-mono">{r.barcode || "—"}</td>
                                            <td className="locations-table-mono">{r.location_name || r.location || "—"}</td>
                                            <td className="locations-table-mono">{r.tote_id || "—"}</td>
                                            <td>{r.po_number || "—"}</td>
                                            <td>{r.received_at ? formatDateTime(r.received_at) : "—"}</td>
                                            <td className="locations-table-qty">{onHand}</td>
                                            <td className="locations-table-qty">{allocated}</td>
                                            <td className="locations-table-qty">{Number(r.reserved_projected) || 0}</td>
                                            <td className="locations-table-qty">{unsellable}</td>
                                            <td className="locations-table-qty">{onPallet}</td>
                                            <td className="locations-table-qty">{remaining + allocated}</td>
                                            <td className="locations-table-num">{formatMoney(unitCost, r.currency || "GBP")}</td>
                                            <td className="locations-table-num">{formatMoney(remaining * unitCost, r.currency || "GBP")}</td>
                                            <td>{t(`locations_value_${sellable}`)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </>
                    )}

                    {tab === "locations" && (
                        <>
                            <thead>
                                <tr>
                                    <th>{t("inventory_col_warehouse")}</th>
                                    <th>{t("inventory_col_sku")}</th>
                                    <th>{t("inventory_col_barcode")}</th>
                                    <th>{t("inventory_col_location")}</th>
                                    <th>{t("inventory_col_tote")}</th>
                                    <th className="locations-table-qty">{t("inventory_col_on_hand")}</th>
                                    <th className="locations-table-qty">{t("inventory_col_allocated")}</th>
                                    <th className="locations-table-qty" title={t("inventory_reserved_projected_hint")}>{t("inventory_col_reserved_projected")}</th>
                                    <th className="locations-table-qty">{t("inventory_col_unsellable")}</th>
                                    <th className="locations-table-qty" title={t("inventory_on_pallet_hint")}>{t("inventory_col_on_pallet")}</th>
                                    <th className="locations-table-qty">{t("inventory_col_total")}</th>
                                    <th className="locations-table-num">{t("inventory_col_unit_cost")}</th>
                                    <th className="locations-table-num">{t("inventory_col_total_cost")}</th>
                                    <th>{t("inventory_col_sellable")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleLocations.map(r => {
                                    // Weighted-average unit cost across the receipts at this
                                    // location — same approach as the Inventory Summary tab.
                                    const remainingQty = r.on_hand + r.unsellable + (Number(r.channel_excluded) || 0);
                                    const unitCost = remainingQty > 0 ? r.total_cost / remainingQty : null;
                                    return (
                                        <tr key={`${r.sku}|${r.warehouse}|${r.location}`}>
                                            <td>{r.warehouse}</td>
                                            <td className="locations-table-mono">{r.sku}</td>
                                            <td className="locations-table-mono">{r.barcode || "—"}</td>
                                            <td className="locations-table-mono">{r.location_name || r.location}</td>
                                            <td className="locations-table-mono">{r.tote_id || "—"}</td>
                                            <td className="locations-table-qty">{r.on_hand}</td>
                                            <td className="locations-table-qty">{r.allocated}</td>
                                            <td className="locations-table-qty">{Number(r.reserved_projected) || 0}</td>
                                            <td className="locations-table-qty">{r.unsellable}</td>
                                            <td className="locations-table-qty">{Number(r.channel_excluded) || 0}</td>
                                            <td className="locations-table-qty">{r.total}</td>
                                            <td className="locations-table-num">{unitCost != null ? formatMoney(unitCost) : "—"}</td>
                                            <td className="locations-table-num">{formatMoney(r.total_cost)}</td>
                                            <td>{t(`locations_value_${r.sellable}`)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </>
                    )}
                </table>
            </div>

            {!loading && activeRows.length === 0 && (
                <div className="locations-table-empty">
                    {tab === "stock" && t("inventory_no_stock")}
                    {tab === "detail" && t("inventory_no_receipts")}
                    {tab === "locations" && t("inventory_no_locations")}
                </div>
            )}

            {activeRows.length > 0 && (
                <div className="locations-table-footer">
                    <span>{t("locations_showing_range", {
                        from: String(rangeFrom),
                        to: String(rangeTo),
                        total: String(activeRows.length),
                    })}</span>
                    <div className="locations-table-pagination">
                        <button
                            type="button"
                            className="locations-table-page-btn"
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                            disabled={safePage === 0}
                            aria-label={t("pagination_previous")}
                        >
                            <ChevronLeft size={16} />
                            <span>{t("pagination_previous")}</span>
                        </button>
                        <span className="locations-table-page-indicator">
                            {t("pagination_page_x_of_y", {
                                page: String(safePage + 1),
                                total: String(totalPages),
                            })}
                        </span>
                        <button
                            type="button"
                            className="locations-table-page-btn"
                            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={safePage >= totalPages - 1}
                            aria-label={t("pagination_next")}
                        >
                            <span>{t("pagination_next")}</span>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryAdmin;
